"use client";

import { getSiteUiCopy } from "@/lib/site-ui-copy";

import Image from "next/image";
import Link from "@/components/ui/CcLink";
import { useLenis } from "lenis/react";
import { usePathname } from "next/navigation";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type WheelEvent,
} from "react";
import { CcNavInlineDetailLabel } from "@/components/layout/CcNavInlineDetailLabel";
import { useBlogLocaleNavigation } from "@/components/layout/CcLocaleContext";
import { CcNavMobileMenu } from "@/components/layout/CcNavMobileMenu";
import { CcNavProjectsMenu } from "@/components/layout/CcNavProjectsMenu";
import { CcNavResourcesMenu } from "@/components/layout/CcNavResourcesMenu";
import { CcNavServicesMenu } from "@/components/layout/CcNavServicesMenu";
import { CcButton } from "@/components/ui/CcButton";
import { useOpensBookCallPopup } from "@/lib/use-opens-book-call-popup";
import {
  CC_NAV_THEME_LINE,
  CC_NAV_THEME_SELECTOR,
  getNavOverDarkSectionWithFallback,
} from "@/lib/cc-nav-hero";
import { getNavMobileMenuCopy } from "@/lib/cc-nav-mobile-menu";
import { getNavProjectsMegaCopy } from "@/lib/cc-nav-projects-mega-copy";
import { flattenNavResourceMegaLinks, getNavResourcesMegaCopy } from "@/lib/cc-nav-resources-mega";
import { getNavServicesMegaCopy } from "@/lib/cc-nav-services-mega";
import { shouldUseCompactServicesNav } from "@/lib/cc-nav-publish";
import { isResourcesNavTriggerHref } from "@/lib/cc-nav-resources";
import type { NavResourceItem } from "@/lib/cc-nav-resources";
import type { NavResourceMegaColumn } from "@/lib/cc-nav-resources-mega";
import type { NavProjectMegaColumn } from "@/lib/cc-nav-projects-mega";
import type { NavProjectItem } from "@/lib/projects";
import type { NavServiceMegaColumn } from "@/lib/cc-nav-services-mega";
import type { NavServiceItem } from "@/lib/services";
import { useCcScrollEffect } from "@/lib/use-cc-scroll-effect";
import { toSiteLocale } from "@/lib/i18n/config";
import {
  navBreadcrumbRememberedDetail,
  resolveNavBreadcrumb,
  resolveNavbarBreadcrumbForPathname,
  shouldHoldNavDetailFallback,
  type NavBreadcrumbTrail,
} from "@/lib/cc-nav-breadcrumb";
import {
  forgetNavDetailText,
  getRememberedNavDetailText,
  rememberNavDetailText,
} from "@/lib/cc-nav-detail-hold";
import { matchesLocalizedNavPath } from "@/lib/i18n/paths";
import type { BookCallPopupVariant } from "@/lib/book-call-popup-copy";
import { navExpandCompactCtaWidthCssVar } from "@/lib/nav-expand-compact-cta-width";
import type { Lang } from "@/lib/types";

/** Matches `$cc-breakpoint-md` in `src/styles/_variables.scss` (810px). */
const CC_NAV_DESKTOP_LAYOUT_MQ = "(min-width: 811px)";

/** Ignore subpixel/layout noise when deciding if the link rail scrolls. */
const CC_NAV_LINKS_OVERFLOW_EPSILON_PX = 1;

type CcNavbarProps = {
  lang: Lang;
  links: { label: string; href: string }[];
  serviceItems: NavServiceItem[];
  serviceMegaColumns: NavServiceMegaColumn[];
  projectItems: NavProjectItem[];
  projectMegaColumns: NavProjectMegaColumn[];
  resourceItems: NavResourceItem[];
  resourceMegaColumns: NavResourceMegaColumn[];
  ctaLabel: string;
  ctaHref: string;
  ctaIcon?: "arrow" | "calendar";
  bookCallVariant?: BookCallPopupVariant;
  /** Page starts over a dark section — SSR defaults nav to on-dark to avoid a flash */
  hasDarkHero?: boolean;
  /** Server pathname for stable active links before `usePathname()` hydrates */
  initialPathname?: string;
  /** Server-resolved breadcrumb for `initialPathname` (full blog post titles). */
  initialNavBreadcrumb?: NavBreadcrumbTrail | null;
  variant?: "default" | "quorum";
  /** Dev hero lab: nav overlays the preview frame only (not viewport-fixed / scroll-themed). */
  embedInHeroFrame?: boolean;
};

export function CcNavbar({
  lang,
  links,
  serviceItems,
  serviceMegaColumns,
  projectItems,
  projectMegaColumns = [],
  resourceItems,
  resourceMegaColumns,
  ctaLabel,
  ctaHref,
  ctaIcon = "arrow",
  bookCallVariant = "default",
  hasDarkHero = false,
  initialPathname = "/",
  initialNavBreadcrumb = null,
  variant = "default",
  embedInHeroFrame = false,
}: CcNavbarProps) {
  const uiCopy = getSiteUiCopy(lang).navigation;
  const locale = toSiteLocale(lang);
  const servicesMegaCopy = getNavServicesMegaCopy(lang);
  const projectsMegaCopy = getNavProjectsMegaCopy(lang);
  const resourcesMegaCopy = getNavResourcesMegaCopy(lang);
  const mobileMenuCopy = getNavMobileMenuCopy(lang);
  const compactServicesNav = shouldUseCompactServicesNav(serviceMegaColumns);
  const resourceMegaLinks = flattenNavResourceMegaLinks(resourceMegaColumns);
  const clientPathname = usePathname();
  const pathname = clientPathname ?? initialPathname;
  const { navigation: blogNavigation } = useBlogLocaleNavigation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [desktopNavLayout, setDesktopNavLayout] = useState(false);
  const [detailRailByHref, setDetailRailByHref] = useState<
    Record<string, boolean>
  >({});
  const [heldDetailByHref, setHeldDetailByHref] = useState<
    Record<string, string>
  >({});
  const [mounted, setMounted] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [overDarkSection, setOverDarkSection] = useState(hasDarkHero);
  const navLinksRef = useRef<HTMLElement>(null);
  const [navLinksOverflow, setNavLinksOverflow] = useState(false);
  const lenis = useLenis();

  const handleNavLinksWheel = useCallback(
    (event: WheelEvent<HTMLElement>) => {
      if (variant === "quorum" || !desktopNavLayout || !navLinksOverflow) return;

      const el = navLinksRef.current;
      if (
        !el ||
        el.scrollWidth <= el.clientWidth + CC_NAV_LINKS_OVERFLOW_EPSILON_PX
      ) {
        return;
      }

      const { deltaX, deltaY } = event;
      if (Math.abs(deltaY) < Math.abs(deltaX)) return;

      const maxScroll = el.scrollWidth - el.clientWidth;
      const atStart = el.scrollLeft <= 0;
      const atEnd = el.scrollLeft >= maxScroll - 1;
      const towardStart = deltaY < 0;
      const towardEnd = deltaY > 0;

      if ((towardStart && atStart) || (towardEnd && atEnd)) return;

      el.scrollLeft += deltaY;
      event.preventDefault();
    },
    [desktopNavLayout, navLinksOverflow, variant],
  );

  useEffect(() => {
    setMounted(true);
  }, []);

  useLayoutEffect(() => {
    const syncNavTheme = () => {
      if (embedInHeroFrame) {
        setOverDarkSection(hasDarkHero);
        return;
      }
      setOverDarkSection(getNavOverDarkSectionWithFallback(hasDarkHero));
    };

    syncNavTheme();

    if (embedInHeroFrame) {
      return;
    }

    const sections = document.querySelectorAll(CC_NAV_THEME_SELECTOR);
    if (sections.length === 0) return;

    const observer = new IntersectionObserver(syncNavTheme, {
      rootMargin: `-${CC_NAV_THEME_LINE}px 0px 0px 0px`,
      threshold: [0, 0.01, 1],
    });

    sections.forEach((section) => observer.observe(section));

    return () => {
      observer.disconnect();
    };
  }, [hasDarkHero, pathname, embedInHeroFrame]);

  useEffect(() => {
    if (!embedInHeroFrame) return;
    setOverDarkSection(hasDarkHero);
  }, [embedInHeroFrame, hasDarkHero]);

  useCcScrollEffect(() => {
    if (embedInHeroFrame) return;
    setScrolled(window.scrollY > 20);
    setOverDarkSection(getNavOverDarkSectionWithFallback(hasDarkHero));
  });

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!lenis) return;

    if (menuOpen) lenis.stop();
    else lenis.start();

    return () => {
      lenis.start();
    };
  }, [menuOpen, lenis]);

  const homeHref = links[0]?.href ?? "/";
  useEffect(() => {
    const mq = window.matchMedia(CC_NAV_DESKTOP_LAYOUT_MQ);
    const sync = () => setDesktopNavLayout(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  const clientBreadcrumb =
    variant === "default" ? resolveNavBreadcrumb(pathname, lang, links, { blogNavigation }) : null;
  const onInitialPath = pathname === initialPathname;
  const breadcrumb = resolveNavbarBreadcrumbForPathname(
    pathname,
    initialPathname,
    initialNavBreadcrumb,
    clientBreadcrumb,
  );
  const usesInitialNavBreadcrumb =
    onInitialPath && initialNavBreadcrumb != null;

  if (breadcrumb) {
    rememberNavDetailText(
      breadcrumb.parent.href,
      navBreadcrumbRememberedDetail(breadcrumb),
    );
  }

  const isActive = (href: string) => {
    if (href === homeHref) return pathname === href;
    if (isResourcesNavTriggerHref(href, locale)) {
      return resourceMegaLinks.some(
        (item) =>
          pathname === item.href || pathname.startsWith(`${item.href}/`),
      );
    }
    return pathname === href || pathname.startsWith(`${href}/`) || breadcrumb?.parent.href === href;
  };

  const breadcrumbForLink = (href: string) =>
    breadcrumb && breadcrumb.parent.href === href ? breadcrumb : null;

  const detailInitiallySettledForLink = (href: string) =>
    onInitialPath && breadcrumbForLink(href) != null;

  const detailTitleForLink = (href: string) =>
    breadcrumbForLink(href)?.current ?? null;

  useLayoutEffect(() => {
    setHeldDetailByHref((prev) => {
      let next: Record<string, string> | null = null;

      for (const link of links) {
        const live = detailTitleForLink(link.href);
        if (live) {
          if (prev[link.href] === live) continue;
          if (!next) next = { ...prev };
          next[link.href] = live;
          continue;
        }

        // Keep held while exit animation runs (detail rail still active).
        if (detailRailByHref[link.href]) continue;

        if (prev[link.href]) {
          if (!next) next = { ...prev };
          delete next[link.href];
        }
      }

      return next ?? prev;
    });
  }, [breadcrumb, links, detailRailByHref]);

  const detailPropsForLink = (href: string) => {
    const live = detailTitleForLink(href);
    const remembered = onInitialPath ? null : getRememberedNavDetailText(href);
    const held = heldDetailByHref[href] ?? remembered;
    const holdForExit = shouldHoldNavDetailFallback(
      href,
      pathname,
      initialPathname,
      live,
      held,
      Boolean(detailRailByHref[href]),
      isActive,
    );

    const trail = breadcrumbForLink(href);

    return {
      detailTitle: live,
      detailLead: trail?.middle?.label ?? null,
      detailLeadHref: trail?.middle?.href ?? null,
      detailTitleFallback: live ? null : holdForExit ? held : null,
    };
  };

  const handleDetailRailChange = useCallback((href: string, active: boolean) => {
    setDetailRailByHref((prev) => {
      if (prev[href] === active) return prev;
      if (!active) {
        forgetNavDetailText(href);
      }
      return { ...prev, [href]: active };
    });
  }, []);

  const showBreadcrumbLayout =
    desktopNavLayout &&
    (Boolean(breadcrumb) || Object.values(detailRailByHref).some(Boolean));

  const syncNavLinksOverflow = useCallback(() => {
    const el = navLinksRef.current;
    if (!el || variant === "quorum" || !desktopNavLayout) {
      setNavLinksOverflow((prev) => (prev ? false : prev));
      return;
    }

    const overflows =
      el.scrollWidth > el.clientWidth + CC_NAV_LINKS_OVERFLOW_EPSILON_PX;
    setNavLinksOverflow((prev) => (prev === overflows ? prev : overflows));

    if (!overflows && el.scrollLeft !== 0) {
      el.scrollLeft = 0;
    }
  }, [desktopNavLayout, variant]);

  useLayoutEffect(() => {
    syncNavLinksOverflow();

    const el = navLinksRef.current;
    if (!el || variant === "quorum" || !desktopNavLayout) {
      return;
    }

    const ro = new ResizeObserver(() => {
      syncNavLinksOverflow();
    });
    ro.observe(el);
    const list = el.querySelector(".cc-nav__links-list");
    if (list instanceof Element) {
      ro.observe(list);
    }

    window.addEventListener("resize", syncNavLinksOverflow);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", syncNavLinksOverflow);
    };
  }, [
    syncNavLinksOverflow,
    desktopNavLayout,
    variant,
    pathname,
    showBreadcrumbLayout,
    detailRailByHref,
    heldDetailByHref,
    links,
  ]);

  const opensBookCallPopup = useOpensBookCallPopup(ctaLabel);
  const navCtaWidth = navExpandCompactCtaWidthCssVar(ctaLabel);
  const megaMountRef = useRef<HTMLDivElement>(null);

  return (
    <header
      className={`cc-nav ${variant === "quorum" ? "cc-nav--quorum" : ""} ${embedInHeroFrame ? "cc-nav--hero-frame-embed" : ""} ${showBreadcrumbLayout ? "cc-nav--breadcrumb" : ""} ${mounted ? "cc-nav--mounted" : ""} ${overDarkSection ? "cc-nav--on-dark" : ""} ${scrolled ? "cc-nav--scroll-compact" : ""} ${scrolled && !overDarkSection ? "cc-nav--scrolled" : ""}`}
      style={{ "--cc-nav-cta-width": navCtaWidth } as CSSProperties}
    >
      <div className="cc-nav__shell">
      <div
        className={`cc-nav__pill ${menuOpen ? "cc-nav__pill--menu-open" : ""} ${showBreadcrumbLayout ? "cc-nav__pill--breadcrumb" : ""}`}
      >
        <div className="cc-nav__pill-head">
          <Link href={homeHref} priority="primary" className="cc-nav__logo" aria-label={uiCopy.home}>
            <Image
              className="cc-nav__logo-mark"
              src="/assets/imgs/nav-mark.svg"
              alt=""
              width={20}
              height={21}
              priority
            />
            <Image
              className="cc-nav__logo-full"
              src="/assets/imgs/CaptivateClick-logo.svg"
              alt=""
              width={200}
              height={26}
              priority
            />
            <span className="cc-nav__brand-name">CaptivateClick</span>
          </Link>

          <button
            type="button"
            className="cc-nav__toggle"
            aria-label={menuOpen ? uiCopy.closeMenu : uiCopy.openMenu}
            aria-expanded={menuOpen}
            aria-controls="cc-nav-mobile-menu"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span
              className="cc-nav__icon-swap"
              data-state={menuOpen ? "b" : "a"}
              aria-hidden="true"
            >
              <span className="cc-nav__icon-swap-layer cc-nav__icon-swap-layer--menu">
                <span className="cc-nav__menu-line" />
                <span className="cc-nav__menu-line" />
                <span className="cc-nav__menu-line" />
              </span>
              <span className="cc-nav__icon-swap-layer cc-nav__icon-swap-layer--close">
                <span className="cc-nav__close-line cc-nav__close-line--1" />
                <span className="cc-nav__close-line cc-nav__close-line--2" />
              </span>
            </span>
          </button>
        </div>

        <nav
          ref={navLinksRef}
          className={`cc-nav__links${navLinksOverflow ? " cc-nav__links--overflow" : ""}`}
          aria-label={uiCopy.main}
          {...(variant !== "quorum" && desktopNavLayout && navLinksOverflow
            ? { "data-lenis-prevent-vertical": true }
            : {})}
          onWheel={handleNavLinksWheel}
        >
          <ul className="cc-nav__links-list">
            {links.map((link) => {
              const { detailTitle, detailLead, detailLeadHref, detailTitleFallback } =
                detailPropsForLink(link.href);
              const detailInitiallySettled = detailInitiallySettledForLink(
                link.href,
              );
              const showDetailLayout =
                Boolean(detailTitle) ||
                Boolean(detailTitleFallback) ||
                Boolean(detailRailByHref[link.href]);
              return (
              <li
                key={link.href}
                className={
                  showDetailLayout ? "cc-nav__links-item--has-detail" : undefined
                }
              >
                {matchesLocalizedNavPath(link.href, locale, "services") ? (
                  <CcNavServicesMenu
                    label={link.label}
                    href={link.href}
                    isActive={isActive(link.href)}
                    serviceItems={serviceItems}
                    serviceMegaColumns={serviceMegaColumns}
                    megaCopy={servicesMegaCopy}
                    detailTitle={detailTitle}
                    detailLead={detailLead}
                    detailLeadHref={detailLeadHref}
                    detailTitleFallback={detailTitleFallback}
                    detailInitiallySettled={detailInitiallySettled}
                    onDetailRailActiveChange={(active) =>
                      handleDetailRailChange(link.href, active)
                    }
                    megaMountRef={megaMountRef}
                    onNavigate={() => setMenuOpen(false)}
                  />
                ) : matchesLocalizedNavPath(link.href, locale, "projects") ? (
                  <CcNavProjectsMenu
                    label={link.label}
                    href={link.href}
                    isActive={isActive(link.href)}
                    projectItems={projectItems}
                    projectMegaColumns={projectMegaColumns}
                    megaCopy={projectsMegaCopy}
                    detailTitle={detailTitle}
                    detailTitleFallback={detailTitleFallback}
                    detailInitiallySettled={detailInitiallySettled}
                    onDetailRailActiveChange={(active) =>
                      handleDetailRailChange(link.href, active)
                    }
                    megaMountRef={megaMountRef}
                    onNavigate={() => setMenuOpen(false)}
                  />
                ) : isResourcesNavTriggerHref(link.href, locale) ? (
                  <CcNavResourcesMenu
                    label={link.label}
                    href={link.href}
                    isActive={isActive(link.href)}
                    resourceItems={resourceItems}
                    resourceMegaColumns={resourceMegaColumns}
                    megaCopy={resourcesMegaCopy}
                    detailTitle={detailTitle}
                    detailLead={detailLead}
                    detailLeadHref={detailLeadHref}
                    detailTitleFallback={detailTitleFallback}
                    detailInitiallySettled={detailInitiallySettled}
                    onDetailRailActiveChange={(active) =>
                      handleDetailRailChange(link.href, active)
                    }
                    megaMountRef={megaMountRef}
                    onNavigate={() => setMenuOpen(false)}
                  />
                ) : (
                  <Link
                    href={link.href}
                    priority="primary"
                    className={`cc-nav__link ${isActive(link.href) ? "cc-nav__link--active" : ""} ${showDetailLayout ? "cc-nav__link--has-detail" : ""}`}
                    aria-label={
                      detailTitle ?? detailTitleFallback
                        ? detailLead
                          ? `${link.label} / ${detailLead} / ${detailTitle ?? detailTitleFallback}`
                          : `${link.label} / ${detailTitle ?? detailTitleFallback}`
                        : undefined
                    }
                    onNavigate={() => setMenuOpen(false)}
                  >
                    <CcNavInlineDetailLabel
                      label={link.label}
                      detail={detailTitle}
                      detailLead={detailLead}
                      detailFallback={detailTitleFallback}
                      detailInitiallySettled={detailInitiallySettled}
                      onDetailRailActiveChange={(active) =>
                        handleDetailRailChange(link.href, active)
                      }
                    />
                  </Link>
                )}
              </li>
            );
            })}
          </ul>
        </nav>
      </div>

      <div ref={megaMountRef} className="cc-nav__mega-layer" />

      <CcButton
        href={opensBookCallPopup ? undefined : ctaHref}
        bookCallPopup={opensBookCallPopup}
        bookCallVariant={bookCallVariant}
        variant="primary"
        expand
        expandCompact
        leadingIcon={ctaIcon}
        label={ctaLabel}
        className="cc-nav__cta cc-nav__cta--desktop"
      />
      </div>

      <CcNavMobileMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        links={links}
        serviceItems={serviceItems}
        serviceMegaColumns={serviceMegaColumns}
        compactServicesNav={compactServicesNav}
        servicesSeeAllLabel={servicesMegaCopy.seeAll}
        projectMegaColumns={projectMegaColumns}
        projectsSeeAllLabel={projectsMegaCopy.seeAll}
        resourceMegaColumns={resourceMegaColumns}
        resourcesSeeAllLabel={resourcesMegaCopy.seeAll}
        backLabel={mobileMenuCopy.back}
        isActive={isActive}
        locale={locale}
        lang={lang}
        navOnDark={overDarkSection}
      />
    </header>
  );
}
